import httpService from '../httpService';
import type { JournalEntryModel, JournalEntrySourceType } from '../../types/bankAccount';
import { Currency } from '../../types/bankAccount';

export type { JournalEntrySourceType };

/** UI-friendly transaction type; maps to journal entry source types (SAVING_POT_FUND, SAVING_POT_RELEASE, etc.) */
export enum PotTransactionType {
  DEPOSIT = 'DEPOSIT',
  WITHDRAWAL = 'WITHDRAWAL',
  SPENDING = 'SPENDING',
  ADJUSTMENT = 'ADJUSTMENT',
  REFUND = 'REFUND',
}

/** Journal entry source types used for pot operations (ledger alignment) */
export const POT_JOURNAL_SOURCE_TYPES = {
  FUND: 'SAVING_POT_FUND' as const,
  RELEASE: 'SAVING_POT_RELEASE' as const,
} satisfies Record<string, JournalEntrySourceType>;

export enum ContributionFrequency {
  ONCE = 'ONCE',
  MONTHLY = 'MONTHLY',
  QUARTERLY = 'QUARTERLY',
  YEARLY = 'YEARLY',
}

export interface CreatePotDto {
  name: string;
  currency: Currency;
  description?: string;
  color?: string;
  targetAmount?: number;
  // New fields
  depositsEnabled?: boolean;
  withdrawalsEnabled?: boolean;
  automaticDepositEnabled?: boolean;
  requestedAmount?: number;
  requestedFrequency?: ContributionFrequency;
}

export interface UpdatePotDto {
  name?: string;
  description?: string;
  color?: string;
  targetAmount?: number;
}

export interface DepositToPotDto {
  amount: number;
  description?: string;
  deductFromPersonalWallet?: boolean;
  customShares?: Record<number, number>;
}

export interface WithdrawFromPotDto {
  amount: number;
  description?: string;
  creditToPersonalWallet?: boolean;
}

export interface AddParticipantsDto {
  dashboardMemberIds: number[];
}

export interface UpdateParticipantRoleDto {
  isAdmin: boolean;
}

export interface UpdatePotSettingsDto {
  depositsEnabled?: boolean;
  withdrawalsEnabled?: boolean;
  automaticDepositEnabled?: boolean;
  requestedAmount?: number;
  requestedFrequency?: ContributionFrequency;
}

export interface PotParticipant {
  dashboardMemberId: number;
  name: string;
  isAdmin: boolean;
  isActive: boolean;
  sharePercentage: number;
  currentBalance: number;
}

/** Pot transaction view; backed by a journal entry (journalEntryId) */
export interface PotTransaction {
  /** Same as journalEntryId when backed by journal entry */
  transactionId: number;
  /** Journal entry ID (ledger); use this to link to JournalEntryModel */
  journalEntryId: number;
  type: PotTransactionType;
  /** Journal entry source type (e.g. SAVING_POT_FUND, SAVING_POT_RELEASE) */
  sourceType: JournalEntrySourceType;
  amount: number;
  description?: string;
  performedByMemberId: number;
  performedByName: string;
  processedByDashboardMemberId?: number;
  balanceBefore: number;
  balanceAfter: number;
  /** ISO date string; aligns with journal entry entryDate */
  entryDate: string;
  /** @deprecated Prefer entryDate */
  createdAt: string;
}

export interface PotWithDetails {
  potId: number;
  dashboardId: number;
  name: string;
  description?: string;
  color?: string;
  targetAmount?: number;
  totalBalance: number;
  createdAt: string;
  completedAt?: string;
  targetDeadline?: string;
  participants: PotParticipant[];
  /** Optional list of dashboard member IDs linked to this pot (from controller list/detail responses) */
  dashboardMemberIds?: number[];
  transactionCount: number;
  /** Ledger account ID for this pot (used for journal entries) */
  ledgerAccountId: number;
  currency: Currency;
  depositsEnabled: boolean;
  withdrawalsEnabled: boolean;
  automaticDepositEnabled: boolean;
  requestedAmount?: number;
  requestedFrequency?: ContributionFrequency;
}

export interface TransactionHistoryResult {
  transactions: PotTransaction[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

export interface ParticipantBalance {
  dashboardMemberId: number;
  currentBalance: number;
  sharePercentage: number;
  totalDeposits: number;
  totalWithdrawals: number;
  netContribution: number;
}

export interface BalanceIntegrityCheck {
  potId: number;
  totalBalance: number;
  sumOfParticipantBalances: number;
  isBalanced: boolean;
  discrepancy: number;
}

const potService = {
  /**
   * Create a new pot
   */
  createPot: async (dashboardId: number, createDto: CreatePotDto): Promise<PotWithDetails> => {
    const { data } = await httpService.post<PotWithDetails>(
      `/networks/dashboards/${dashboardId}/saving-pots`,
      createDto,
    );
    return data;
  },

  /**
   * List all pots for a dashboard.
   * @param includeInactive - If true, returns both active and inactive pots (sends activeOnly=false). If false, returns only active pots (activeOnly=true).
   */
  listPots: async (dashboardId: number, includeInactive = false): Promise<PotWithDetails[]> => {
    const { data } = await httpService.get<PotWithDetails[] | { savingPots?: PotWithDetails[]; data?: PotWithDetails[]; items?: PotWithDetails[] }>(
      `/networks/dashboards/${dashboardId}/saving-pots`,
      {
        params: { activeOnly: !includeInactive },
      },
    );
    if (Array.isArray(data)) return data;
    const wrapped = data as Record<string, PotWithDetails[] | undefined>;
    if (Array.isArray(wrapped.savingPots)) return wrapped.savingPots;
    if (Array.isArray(wrapped.data)) return wrapped.data;
    if (Array.isArray(wrapped.items)) return wrapped.items;
    return [];
  },

  /**
   * Get a single pot by ID.
   * Handles both direct response and wrapped { data: Pot } from the API.
   */
  getPot: async (dashboardId: number, potId: number): Promise<PotWithDetails> => {
    const { data } = await httpService.get<PotWithDetails | { data: PotWithDetails }>(
      `/networks/dashboards/${dashboardId}/saving-pots/${potId}`,
    );
    const wrapped = data as { data?: PotWithDetails };
    if (wrapped?.data != null && typeof wrapped.data === 'object' && ('potId' in wrapped.data || 'name' in wrapped.data)) {
      return wrapped.data;
    }
    return data as PotWithDetails;
  },

  /**
   * Update a pot
   */
  updatePot: async (
    dashboardId: number,
    potId: number,
    updateDto: UpdatePotDto,
  ): Promise<PotWithDetails> => {
    const { data } = await httpService.patch<PotWithDetails>(
      `/networks/dashboards/${dashboardId}/saving-pots/${potId}`,
      updateDto,
    );
    return data;
  },

  /**
   * Add participants to a pot
   */
  addParticipants: async (
    dashboardId: number,
    potId: number,
    addDto: AddParticipantsDto,
  ): Promise<void> => {
    await httpService.post(
      `/networks/dashboards/${dashboardId}/saving-pots/${potId}/participants`,
      addDto,
    );
  },

  /**
   * Remove a participant from a pot
   */
  removeParticipant: async (
    dashboardId: number,
    potId: number,
    dashboardMemberId: number,
  ): Promise<void> => {
    await httpService.delete(
      `/networks/dashboards/${dashboardId}/saving-pots/${potId}/participants/${dashboardMemberId}`,
    );
  },

  /**
   * Update participant role (admin status)
   */
  updateParticipantRole: async (
    dashboardId: number,
    potId: number,
    dashboardMemberId: number,
    roleDto: UpdateParticipantRoleDto,
  ): Promise<void> => {
    await httpService.patch(
      `/networks/dashboards/${dashboardId}/saving-pots/${potId}/participants/${dashboardMemberId}/role`,
      roleDto,
    );
  },

  /**
   * Delete a pot
   */
  deletePot: async (dashboardId: number, potId: number): Promise<void> => {
    await httpService.delete(`/networks/dashboards/${dashboardId}/saving-pots/${potId}`);
  },

  /**
   * Deposit money into a pot. Backend should create a journal entry with sourceType SAVING_POT_FUND and sourceId = potId.
   */
  depositToPot: async (
    dashboardId: number,
    potId: number,
    depositDto: DepositToPotDto,
  ): Promise<{ transactionId: number; newBalance: number }> => {
    const { data } = await httpService.post<{ transactionId: number; newBalance: number }>(
      `/networks/dashboards/${dashboardId}/saving-pots/${potId}/deposit`,
      depositDto,
    );
    return data;
  },

  /**
   * Withdraw money from a pot. Backend should create a journal entry with sourceType SAVING_POT_RELEASE and sourceId = potId.
   */
  withdrawFromPot: async (
    dashboardId: number,
    potId: number,
    withdrawDto: WithdrawFromPotDto,
  ): Promise<{ transactionId: number; newBalance: number }> => {
    const { data } = await httpService.post<{ transactionId: number; newBalance: number }>(
      `/networks/dashboards/${dashboardId}/saving-pots/${potId}/withdraw`,
      withdrawDto,
    );
    return data;
  },

  /**
   * Get transaction history for a pot (journal-entry backed).
   * Params align with journal entry listing: sourceType (required), fromDate, toDate.
   * Defaults to SAVING_POT_FUND when sourceType is not provided. Pass an array to request multiple (e.g. fund + release).
   */
  getTransactions: async (
    dashboardId: number,
    potId: number,
    options?: {
      page?: number;
      limit?: number;
      /** Filter by journal entry source type (required by API). Defaults to SAVING_POT_FUND. */
      sourceType?: JournalEntrySourceType | JournalEntrySourceType[];
      transactionType?: PotTransactionType;
      fromDate?: Date;
      toDate?: Date;
    },
  ): Promise<TransactionHistoryResult> => {
    const raw = options?.sourceType;
    const sourceTypes = raw != null
      ? (Array.isArray(raw) ? raw : [raw])
      : [POT_JOURNAL_SOURCE_TYPES.FUND, POT_JOURNAL_SOURCE_TYPES.RELEASE];
    const now = new Date();
    const defaultFrom = new Date(now);
    defaultFrom.setFullYear(defaultFrom.getFullYear() - 10);
    const fromDate = options?.fromDate ?? defaultFrom;
    const toDate = options?.toDate ?? now;
    const { data } = await httpService.get<TransactionHistoryResult | { data: TransactionHistoryResult }>(
      `/networks/dashboards/${dashboardId}/saving-pots/${potId}/transactions`,
      {
        params: {
          page: options?.page,
          limit: options?.limit,
          sourceType: sourceTypes.length === 1 ? sourceTypes[0] : sourceTypes,
          transactionType: options?.transactionType,
          fromDate: fromDate.toISOString(),
          toDate: toDate.toISOString(),
        },
      },
    );
    const wrapped = data as { data?: TransactionHistoryResult };
    if (wrapped?.data != null && typeof wrapped.data === 'object' && 'transactions' in wrapped.data) {
      return wrapped.data;
    }
    return data as TransactionHistoryResult;
  },

  /**
   * Get journal entries for a pot (ledger alignment).
   * Fetches from dashboard journal-entries filtered by sourceId = potId and optional sourceType/date range.
   * Backend should support query param sourceId to restrict to pot-related entries.
   */
  getPotJournalEntries: async (
    dashboardId: number,
    potId: number,
    options?: {
      sourceType?: JournalEntrySourceType;
      fromDate?: string;
      toDate?: string;
    },
  ): Promise<JournalEntryModel[]> => {
    const params: Record<string, string> = { sourceId: String(potId) };
    if (options?.sourceType) params.sourceType = options.sourceType;
    if (options?.fromDate) params.fromDate = options.fromDate;
    if (options?.toDate) params.toDate = options.toDate;
    const { data } = await httpService.get<JournalEntryModel[]>(
      `/networks/dashboards/${dashboardId}/journal-entries`,
      { params },
    );
    return data;
  },

  /**
   * Get participant balance details
   */
  getParticipantBalance: async (
    dashboardId: number,
    potId: number,
    dashboardMemberId: number,
  ): Promise<ParticipantBalance> => {
    const { data } = await httpService.get<ParticipantBalance>(
      `/networks/dashboards/${dashboardId}/saving-pots/${potId}/participants/${dashboardMemberId}/balance`,
    );
    return data;
  },

  /**
   * Perform balance integrity check
   */
  performIntegrityCheck: async (
    dashboardId: number,
    potId: number,
  ): Promise<BalanceIntegrityCheck> => {
    const { data } = await httpService.get<BalanceIntegrityCheck>(
      `/networks/dashboards/${dashboardId}/saving-pots/${potId}/integrity-check`,
    );
    return data;
  },

  /**
   * Update pot settings
   */
  updatePotSettings: async (
    dashboardId: number,
    potId: number,
    updateSettingsDto: UpdatePotSettingsDto,
  ): Promise<PotWithDetails> => {
    const { data } = await httpService.put<PotWithDetails>(
      `/networks/dashboards/${dashboardId}/saving-pots/${potId}/settings`,
      updateSettingsDto,
    );
    return data;
  },
};

export default potService;
